import {
  internalChatSound,
  ticketSound
} from "../assets/sounds";

const STORAGE_KEY = "altchatNotificationSoundsMuted";

const soundSources = {
  message: ticketSound,
  ticket: ticketSound,
  internalChat: internalChatSound
};

let audioContext = null;
let buffers = {};
let loadingPromise = null;
let unlockRegistered = false;
let htmlAudios = {};
const subscribers = new Set();

const getAudioContext = () => {
  if (typeof window === "undefined") return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  if (!audioContext) {
    audioContext = new AudioContextClass();
  }

  return audioContext;
};

const loadBuffers = async () => {
  const context = getAudioContext();
  if (!context) return {};

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = Promise.all(
    Object.entries(soundSources).map(async ([key, source]) => {
      if (buffers[key]) return;

      const response = await fetch(source);
      const arrayBuffer = await response.arrayBuffer();
      buffers[key] = await context.decodeAudioData(arrayBuffer);
    })
  )
    .then(() => buffers)
    .catch(() => {
      loadingPromise = null;
      return buffers;
    });

  return loadingPromise;
};

const getHtmlAudio = type => {
  if (typeof window === "undefined") return null;

  const soundType = soundSources[type] ? type : "message";

  if (!htmlAudios[soundType]) {
    const audio = new Audio(soundSources[soundType]);
    audio.preload = "auto";
    audio.muted = false;
    audio.volume = 1;
    htmlAudios[soundType] = audio;
  }

  return htmlAudios[soundType];
};

const primeHtmlAudios = async () => {
  await Promise.all(
    Object.keys(soundSources).map(async type => {
      const audio = getHtmlAudio(type);
      if (!audio) return;

      try {
        const previousMuted = audio.muted;
        const previousVolume = audio.volume;
        audio.muted = true;
        audio.volume = 0;
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        audio.muted = previousMuted;
        audio.volume = previousVolume;
      } catch (error) {
        audio.muted = false;
        audio.volume = 1;
      }
    })
  );
};

const unlock = async () => {
  const context = getAudioContext();

  try {
    if (context && context.state === "suspended") {
      await context.resume();
    }
    await Promise.all([loadBuffers(), primeHtmlAudios()]);
    return !context || context.state === "running";
  } catch (error) {
    return false;
  }
};

export const ensureNotificationSoundReady = () => {
  if (typeof window === "undefined" || unlockRegistered) return;

  unlockRegistered = true;
  const unlockOnce = () => {
    void unlock();
  };

  window.addEventListener("pointerdown", unlockOnce, { passive: true });
  window.addEventListener("keydown", unlockOnce, { passive: true });
  window.addEventListener("touchstart", unlockOnce, { passive: true });
};

export const isNotificationSoundMuted = () => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STORAGE_KEY) === "true";
};

export const setNotificationSoundMuted = muted => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, muted ? "true" : "false");
  }

  subscribers.forEach(callback => callback(Boolean(muted)));
};

export const subscribeNotificationSoundMuted = callback => {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
};

export const playNotificationSound = async type => {
  if (isNotificationSoundMuted()) return false;

  const source = soundSources[type] || soundSources.message;
  const context = getAudioContext();

  if (context) {
    try {
      if (context.state !== "running") {
        await context.resume();
      }

      await loadBuffers();
      const buffer = buffers[type] || buffers.message;

      if (buffer && context.state === "running") {
        const sourceNode = context.createBufferSource();
        const gainNode = context.createGain();
        sourceNode.buffer = buffer;
        gainNode.gain.value = 1;
        sourceNode.connect(gainNode);
        gainNode.connect(context.destination);
        sourceNode.start(0);
        return true;
      }
    } catch (error) {
      // HTMLAudio fallback below.
    }
  }

  try {
    const audio = getHtmlAudio(type) || new Audio(source);
    audio.pause();
    audio.currentTime = 0;
    audio.muted = false;
    audio.volume = 1;
    await audio.play();
    return true;
  } catch (error) {
    return false;
  }
};
